import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuill } from '../editor/QuillContext';

const PostTOCContext = React.createContext<{
  isTOCOpen: boolean;
  toggleTOC: (forceValue?: boolean) => void;
}>({ isTOCOpen: false, toggleTOC: () => {} });

export const usePostTOC = () => useContext(PostTOCContext);

interface UsePostTOCStateOptions {
  initialState: boolean;
  onToggle?: () => void;
}

const usePostTOCState = (
  { initialState, onToggle }: UsePostTOCStateOptions = { initialState: false },
) => {
  const onToggleRef = useRef(onToggle);
  useLayoutEffect(() => {
    onToggleRef.current = onToggle;
  }, [onToggle]);

  const { quill } = useQuill();

  const [isTOCOpen, setIsTOCOpen] = useState(initialState);

  const toggleTOC = useCallback((forceValue?: boolean) => {
    setIsTOCOpen((current) =>
      typeof forceValue === 'boolean' ? forceValue : !current,
    );
    onToggleRef.current?.();
  }, []);

  useEffect(() => {
    if (quill != null && !quill.root.querySelector('h1, h2, h3')) {
      setIsTOCOpen(false);
    }
  }, [quill]);

  return useMemo(
    () => ({
      isTOCOpen,
      toggleTOC,
    }),
    [isTOCOpen, toggleTOC],
  );
};

interface TOCProviderProvider {
  children: React.ReactNode;
}

const PostTOCProvider = ({ children }: TOCProviderProvider) => {
  const value = usePostTOCState();
  return (
    <PostTOCContext.Provider value={value}>{children}</PostTOCContext.Provider>
  );
};

export default PostTOCProvider;
